import { MessageCircleHeart, ClipboardList, Landmark, Sparkles, ArrowUpRight, Lock, QrCode, HeartHandshake } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import { Link } from "react-router-dom";
import { FRANCIS_CONNECT_URL, FAMILY_SURVEY_URL, GKK_PORTAL_URL, YOUTH_PULSE_URL, PARISH_ATTENDANCE_URL, PASTORAL_CARE_URL } from "@/data/links";

const APPS = [
  {
    id: "francis-connect",
    icon: MessageCircleHeart,
    name: "FrancisConnect",
    who: "For everyone",
    desc: "The parish's AI assistant. Ask about Mass times, sacraments, GKKs and ministries, and read today's Gospel and Saint of the Day — any time of day.",
    cta: "Ask FrancisConnect",
    url: FRANCIS_CONNECT_URL,
    tone: "bg-cocoa-900 text-parchment-100",
  },
  {
    id: "parish-attendance",
    icon: QrCode,
    name: "Parish Attendance",
    who: "For meeting participants",
    desc: "Record your presence at parish meetings and assemblies — enter the Meeting ID or scan the QR code at the venue. Participants can also review their attendance history.",
    cta: "Register attendance",
    url: PARISH_ATTENDANCE_URL,
    tone: "bg-parchment-50 text-cocoa-900 border border-cocoa-900/25",
  },
  {
    id: "family-survey",
    icon: ClipboardList,
    name: "Family Survey",
    who: "For every parish household",
    desc: "Register your family with the parish. One short form per household helps your GKK and our ministries know and care for you better.",
    cta: "Answer the Survey",
    url: FAMILY_SURVEY_URL,
    tone: "bg-sage-dark text-parchment-100",
  },
  {
    id: "pastoral-care",
    icon: HeartHandshake,
    name: "Mabuhay Care",
    who: "For anyone in need",
    desc: "The parish Pastoral Care and Assistance Center. Ask for help, a confidential conversation, or a home visit for the sick and elderly — or offer to help others.",
    cta: "Reach out to Care",
    url: PASTORAL_CARE_URL,
    tone: "bg-parchment-200 text-cocoa-900 border border-parchment-400",
  },
  {
    id: "gkk-records",
    icon: Landmark,
    name: "GKK Records",
    who: "For GKK & Unit officers",
    desc: "The GKK Financial Management Information System — record and monitor collections, remittances and expenses with transparency and stewardship.",
    cta: "Officer sign-in",
    url: GKK_PORTAL_URL,
    tone: "bg-parchment-50 text-cocoa-900 border border-parchment-400",
    restricted: true,
  },
  {
    id: "youth-pulse",
    icon: Sparkles,
    name: "Youth Pulse",
    who: "For young people",
    desc: "A safe space to ask anything, seek guidance and talk with Kura Paruko, our parish priest — in English, Cebuano or Filipino.",
    cta: "Ask Kura Paruko",
    url: YOUTH_PULSE_URL,
    tone: "bg-terracotta text-parchment-100",
  },
];

const AppCard = ({ a, i }) => {
  const Icon = a.icon;
  const light = a.tone.includes("bg-parchment");
  return (
    <a
      href={a.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`parish-app-${a.id}`}
      style={{ animationDelay: `${i * 90}ms` }}
      className={`group relative flex flex-col p-8 min-h-[300px] ${a.wide ? "lg:col-span-3" : "lg:col-span-2"} ${a.tone} hover:-translate-y-1.5 transition-transform duration-500 animate-fade-up`}
    >
      <div className="flex items-start justify-between">
        <Icon size={30} strokeWidth={1.3} className={light ? "text-terracotta" : "text-gold"} />
        <ArrowUpRight size={20} className="opacity-60 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
      <div className={`mt-8 text-[10px] uppercase tracking-[0.28em] ${light ? "text-sage-dark" : "opacity-75"} flex items-center gap-1.5`}>
        {a.restricted && <Lock size={10} />} {a.who}
      </div>
      <h3 className="mt-2 font-serif text-3xl leading-tight">{a.name}</h3>
      <p className={`mt-4 font-sans font-light text-[15px] leading-relaxed ${light ? "text-cocoa-700" : "opacity-85"}`}>{a.desc}</p>
      <span className="mt-auto pt-8 text-[11px] uppercase tracking-[0.22em] font-sans border-b border-current self-start pb-0.5">
        {a.cta}
      </span>
    </a>
  );
};

export default function ParishApps() {
  return (
    <section id="parish-apps" data-testid="parish-apps-section" className="py-24 md:py-32 bg-parchment-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="Online Services"
          title="Parish Apps"
          intro="Six ways to stay connected with Saint Francis of Assisi Parish — wherever you are."
        />
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-6 gap-5">
          {APPS.map((a, i) => <AppCard key={a.id} a={a} i={i} />)}
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.22em] font-sans text-cocoa-700">
          <span className="text-sage-dark">Printable posters for chapels:</span>
          <Link to="/survey-poster" data-testid="apps-survey-poster-link" className="hover:text-terracotta border-b border-current pb-0.5">Family Survey</Link>
          <Link to="/care-poster" data-testid="apps-care-poster-link" className="hover:text-terracotta border-b border-current pb-0.5">Mabuhay Care home visits</Link>
        </div>
      </div>
    </section>
  );
}
